// libs
import { RefObject, useEffect } from 'react'

export const SIGNUP_CTA_SPACE_VARIABLE = '--signup-cta-space'

/**
 * Publishes how much room the sign-up CTA takes up at the bottom of the viewport — its own
 * height plus the gap it keeps from the edge and the safe area it pads for — as a custom
 * property on the document root.
 *
 * Page bottom spacing and the floating action button both read that property, so the space a
 * page reserves and the distance the button moves up cannot drift apart. While the CTA is not
 * rendered the property is removed and both fall back to their own value.
 */
export const useSignupCtaSpace = (ref: RefObject<HTMLElement>, active: boolean) => {
  useEffect(() => {
    const root = document.documentElement
    const clear = () => root.style.removeProperty(SIGNUP_CTA_SPACE_VARIABLE)
    const element = ref.current

    if (!active || !element) {
      clear()
      return clear
    }

    const publish = () => {
      const { top, height } = element.getBoundingClientRect()
      const space = height > 0 ? Math.round(window.innerHeight - top) : 0
      root.style.setProperty(SIGNUP_CTA_SPACE_VARIABLE, `${space}px`)
    }

    publish()

    if (typeof ResizeObserver === 'undefined') {
      return clear
    }

    const observer = new ResizeObserver(publish)
    observer.observe(element)

    return () => {
      observer.disconnect()
      clear()
    }
  }, [ref, active])
}
